import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { connect } from "react-redux";
import { Toaster } from "react-hot-toast";
import { BrowserRouter } from "react-router-dom";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { Elements, RecurlyProvider } from "@recurly/react-recurly";
import { sha1 } from "locutus/php/strings";
import axios from "axios";
import { ErrorBoundary } from "react-error-boundary";
import Lottie from "lottie-react";

import { useScript } from "./utils/script";
import { apiCallV2 } from "./utils/api";
import { socket } from "./utils/socket";
import { TOAST_CONFIGS, CustomToaster } from "./utils/toast";
import { clearLocalStorage } from "./utils/localstorage";
import { getBRGPatientCookie, setUpCookiesFromInvitation } from "./utils/cookie";

import { LoadUserData, GetWorkOutHistory, LoadUserBillingData, GetInAppNotifications, InAppNotificationClicked, AddUserLocation, LoadAccountDetails, SetInvitationDetails, GetPatientEmailDetails, } from "./redux/v2/user/actions";
import { CastSocketTakeAction, CastDeviceCheck, UpdateAirplayAvailability, UpdateAirplayConnectivity, UpdateCastAvailability } from "./redux/v2/cast/actions";
import { GetWorkoutDetails } from "./redux/v2/workout/actions";
import { GetPrograms } from "./redux/v2/program/actions";
import { SetDeviceType, SetShowLocationPromptOverlay, SetShowInAppNotificationOverlay, SetInAppNotificationOverlayData, GetEligibleLocations, SetVPTStatus, CheckLocationEligibility, SetLocation, NewError, } from "./redux/v2/utils/actions";

import CustomSlider from "./common/CustomSlider";
import useSession from "./hooks/useSession";
import AppNotifPermPrompt from "./pages/settings/notification/components/AppNotifPermPrompt";
import InAppNotification from "./pages/settings/notification/components/InAppNotification";
import ErrorAnimation from "./styles/animations/error-animation.json";
import Routes from "./routes";

const getDeviceType = () => {
    const ua = navigator.userAgent;
    if (/(tablet|ipad|playbook|silk)|(android(?!.*mobi))/i.test(ua)) {
        if (ua.toLowerCase().indexOf("ipad") !== -1) {
            return "ios";
        } else {
            return "tablet";
        }
    }
    if (/Mobile|iP(hone|od)|Android|BlackBerry|IEMobile|Kindle|Silk-Accelerated|(hpw|web)OS|Opera M(obi|ini)/.test(ua)) {
        if (ua.toLowerCase().indexOf("android") !== -1) {
            return "android";
        } else if (ua.toLowerCase().indexOf("iphone") !== -1) {
            return "ios";
        } else if (ua.toLowerCase().indexOf("ipad") !== -1) {
            return "ios";
        } else {
            return "mobile";
        }
    }
    return "desktop";
};


function Fallback(props) {
    const { error, resetErrorBoundary } = props;

    useEffect(() => {
        props.NewError({
            message: error.message,
            stack: error.stack,
        });
    }, [error]);

    return (
        <div role="alert" id="error-boundary-fallback">
            <div>
                <Lottie className="high-five-animation" animationData={ErrorAnimation} loop={true} />
                <p>Something went wrong:</p>
            </div>
        </div>
    );
}

function AppWrapper(props) {
    return (
        <ErrorBoundary
            FallbackComponent={(errorProps) => <Fallback {...errorProps} {...props} />}
            onReset={(details) => {
                // Reset the state of your app so the error doesn't happen again
            }}
        >
            <App {...props} />
        </ErrorBoundary>
    )
}

const App = (props) => {
    console.log(`UPDATE(${process.env.REACT_APP_ENVIRONMENT}) - 2025-04-29:2`);

    useScript("identify.js");
    useScript("conversion.js");
    useScript("planStart.js");
    useScript("smartbanner.js");

    const { account } = props.User;
    const { showInAppNotificationOverlay, inAppNotificationOverlayData } = props.Utils;
    const dvctype = getDeviceType();
    const { sessionStart, sessionEnd } = useSession();

    localStorage.setItem("indi-device-type", dvctype);

    const [showSlider, setShowSlider] = useState(false);
    const [showShareButton, setShowShareButton] = useState(true);
    const [hasNotificationPermission, setHasNotificationPermission] = useState(null);
    const platform = new URLSearchParams(window.location.search).get("platform");
    const access_token = new URLSearchParams(window.location.search).get("access_token");
    const irclickid = new URLSearchParams(window.location.search).get("irclickid");
    const reload = new URLSearchParams(window.location.search).get("reload");
    const isPaid = new URLSearchParams(window.location.search).get("isPaid");
    const notifPrompt = localStorage.getItem("notif-prompt");
    const hasNoNotifPermission = hasNotificationPermission == "0" || hasNotificationPermission == null;
    const saleID = new URLSearchParams(window.location.search).get("sale-id");
    const isiAN = new URLSearchParams(window.location.search).get("ian");

    if (isPaid && isPaid == 1) {
        localStorage.setItem("is-paid", isPaid);
    }

    if (irclickid) {
        localStorage.setItem("irclickid", irclickid);
    }

    if (!localStorage.getItem("indi-platform")) {
        localStorage.setItem("indi-platform", platform);
    }

    if (access_token) {
        localStorage.setItem("access-token", access_token);
    }

    useEffect(() => {
        setUpCookiesFromInvitation();
        localStorage.setItem("sale-id", saleID);

        window?.callIdentify?.();
        props.SetDeviceType();

        if (localStorage.getItem("access-token") || access_token) {
            props.LoadAccountDetails();
            props.LoadUserData();
            props.LoadUserBillingData();
            props.GetPrograms();
        }

        setHasNotificationPermission(localStorage.getItem("has-notification-permission") || new URLSearchParams(window.location.search).get("hasNotificationPermission"));
        localStorage.setItem("has-notification-permission", new URLSearchParams(window.location.search).get("hasNotificationPermission"));
        localStorage.setItem("notification-opted-in", new URLSearchParams(window.location.search).get("isNotifOptedIn"));
        localStorage.setItem("has-location-permission", new URLSearchParams(window.location.search).get("hasLocationPermission"));
        // alert(JSON.stringify(window.location));

        const cb = async () => {
            const brgPatientCookie = await getBRGPatientCookie();

            if (brgPatientCookie) {
                props.SetInvitationDetails(brgPatientCookie);
                brgPatientCookie.emailID && props.GetPatientEmailDetails(brgPatientCookie.emailID);
            }
        }
        cb();


    }, []);

    useEffect(() => {
        if (localStorage.getItem("access-token")) {
            props.LoadAccountDetails();
        }
    }, [reload]);

    useEffect(() => {
        if (((access_token && platform && !notifPrompt && hasNoNotifPermission) || (platform && !notifPrompt && hasNoNotifPermission)) && account?.user?.dob != null) {
            setShowSlider(true);
        }
    }, [access_token, platform, notifPrompt, account?.user?.dob, hasNotificationPermission]);

    useEffect(() => {
        if (localStorage.getItem("access-token") && account?.user?.id) {
            window.callIdentify?.(String(account?.user?.id), sha1(account?.user?.email_address));
            ListenAppCast(account?.user?.id);
            ListenAppShare(account?.user?.id);
            ListenNativeInteraction(account?.user?.id);
        } else {
            window.callIdentify?.();
        }

        if (access_token && platform) {
            props.CastDeviceCheck();
        }

        sessionStart();

    }, [account?.user?.id]);

    useEffect(() => {
        if (access_token && platform) {
            props
                .GetInAppNotifications()
                .then((res) => {
                    if (res) {
                        let { metadata, type, id } = res;
                        metadata = JSON.parse(metadata);

                        const setupOverlay = (type, metadata, appearance) => {
                            props.SetInAppNotificationOverlayData({
                                type: type,
                                metadata: {
                                    ...metadata,
                                    appearance: appearance,
                                },
                                ianID: id,
                            });
                            props.SetShowInAppNotificationOverlay(true);
                        };

                        if (type == "workout-nth") {
                            //metadata?.workout
                            props
                                .GetWorkoutDetails(metadata?.workout)
                                .then((res) => {
                                    setupOverlay("ian", metadata, {
                                        gradient: res?.gradient?.split("-"),
                                        icon: {
                                            type: "number",
                                            svg: ["", "beginner", "moderate", "advanced"][res?.program_detail_id] + res?.svg,
                                        },
                                        title: metadata?.numberOfWorkout == 1 ? "You've completed your first workout" : `You've Completed ${metadata?.numberOfWorkout} Workouts`,
                                        progress: {
                                            workoutsCompleted: metadata?.numberOfWorkout,
                                            activeMinutes: Math.round(metadata?.accumulatedTime / 60),
                                        },
                                    });
                                })
                                .catch((err) => { });
                        } else if (type == "program") {
                            props
                                .GetWorkoutDetails(metadata?.workout)
                                .then((res) => {
                                    setupOverlay("ian", metadata, {
                                        gradient: res?.gradient?.split("-"),
                                        icon: {
                                            type: "program",
                                            svg: ["", "beginner", "moderate", "advanced"][res?.program_detail_id] + res?.svg,
                                        },
                                        title: `You Completed ${res?.title}${["", " - Beginner", " - Moderate", " - Advanced"][res?.program_detail_id]}`,
                                        progress: {
                                            workoutsCompleted: metadata?.numberOfWorkout,
                                            activeMinutes: Math.round(metadata?.accumulatedTime / 60),
                                        },
                                    });
                                })
                                .catch((err) => { });
                        } else if (type == "challenge") {
                            setupOverlay("ian", metadata, {
                                gradient: metadata?.completed?.gradient?.split("-"),
                                icon: {
                                    type: "challenge",
                                    svg: metadata?.completed?.svg,
                                },
                                title: `You've Completed the ${metadata?.completed?.title}`,
                                progress: {
                                    workoutsCompleted: metadata?.numberOfWorkout,
                                    activeMinutes: Math.round(metadata?.accumulatedTime / 60),
                                },
                            });
                        } else if (type == "milestone") {
                            let currentMilestone = metadata?.milestone?.milestone?.split("-")[metadata?.milestone?.currentMilestone - 1];
                            setupOverlay("ian", metadata, {
                                gradient: metadata?.milestone?.gradient?.split("-"),
                                icon: {
                                    type: "challenge",
                                    svg: metadata?.milestone?.svg,
                                },
                                title: `You've Done ${currentMilestone} ${metadata?.milestone?.unit} of the ${metadata?.milestone?.title}`,
                                progress: {
                                    workoutsCompleted: metadata?.numberOfWorkout,
                                    activeMinutes: Math.round(metadata?.accumulatedTime / 60),
                                },
                            });
                        } else if (type == "streak") {
                            props
                                .GetWorkoutDetails(metadata?.workout)
                                .then((res) => {
                                    setupOverlay("ian", metadata, {
                                        gradient: res?.gradient?.split("-"),
                                        icon: {
                                            type: "streak",
                                        },
                                        title: `You've Added to Your Streak`,
                                        progress: {
                                            workoutsCompleted: metadata?.numberOfWorkout,
                                            activeMinutes: Math.round(metadata?.accumulatedTime / 60),
                                        },
                                    });
                                })
                                .catch((err) => { });
                        }
                    }
                })
                .catch((err) => { });
        }
    }, [access_token, platform, isiAN]);

    const ListenAppShare = (id) => {
        socket.removeAllListeners(`app-share-response-${id}`);
        socket.on(`app-share-response-${id}`, (data) => {
            setShowShareButton(true);
        });
    };

    const ListenAppCast = (id) => {
        socket.removeAllListeners(`web-google-cast-${id}`);
        socket.on(`web-google-cast-${id}`, (message) => {
            props.CastSocketTakeAction(message);
        });
    };

    const ListenNativeInteraction = (id) => {
        socket.removeAllListeners(`app-native-component-response-${id}`);
        socket.on(`app-native-component-response-${id}`, async (data) => {
            if (data.action == "INTERACT_NATIVE") {
                if (data.data.type == "notification") {
                    setShowSlider(false);
                    localStorage.setItem("notif-prompt", true);

                    if (data.data?.granted) {
                        localStorage.setItem("has-notification-permission", true);
                        localStorage.setItem("notification-opted-in", 1);
                        setHasNotificationPermission(1);
                        const res = await apiCallV2(
                            "POST",
                            "settings/update",
                            {
                                type: "inAppNotif",
                                value: true,
                            },
                            localStorage.getItem("access-token")
                        );
                    } else {
                        localStorage.setItem("notification-opted-in", 0);
                        setHasNotificationPermission(0);
                    }
                } else if (data.data.type == "airplay") {
                    if (data.data?.hasOwnProperty("isAvailable")) {
                        props.UpdateAirplayAvailability(data.data?.isAvailable);
                    }

                    if (data.data?.hasOwnProperty("isConnected")) {
                        props.UpdateAirplayConnectivity(data.data?.isConnected);
                    }
                } else if (data.data.type == "cast") {
                    if (data.data?.hasOwnProperty("isAvailable")) {
                        props.UpdateCastAvailability(data.data?.isAvailable);
                    }
                    if (data.data?.hasOwnProperty("isConnected")) {
                        props.UpdateAirplayConnectivity(data.data?.isConnected);
                    }
                } else if (data.data.type == "location") {
                    if (data.data?.granted) {
                        const address = data.data?.location?.address;
                        if (address?.region) {
                            props.AddUserLocation(data.data);
                            props.CheckLocationEligibility(address?.region, data.data);
                        } else {
                            const coordinates = data.data?.location?.coordinates;
                            const res = await axios({
                                method: "GET",
                                url: `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${coordinates?.latitude}&lon=${coordinates?.longitude}`,
                            });
                            const location = {
                                address: {
                                    country: res?.data?.address?.country || "",
                                    city: res?.data?.address?.city || "",
                                    region: res?.data?.address?.state || res?.data?.address?.region || "",
                                    timezone: "",
                                    postal_code: res?.data?.address?.postcode || "",
                                },
                                coordinates: {
                                    longitude: coordinates?.longitude,
                                    latitude: coordinates?.latitude,
                                },
                            };

                            props.AddUserLocation({ location: location });
                            props.CheckLocationEligibility(location?.address?.region, location);
                        }
                    } else {
                        props.SetVPTStatus(-1);
                    }
                } else if (data.data.type == "logout") {
                    clearLocalStorage();
                }
            }
        });
    };

    const isAndroid = platform === "android-app" || localStorage.getItem("indi-platform") === "android-app";
    const isMobile = localStorage.getItem("indi-platform") === "android-app" || localStorage.getItem("indi-platform") === "ios-app";
    const tkn = localStorage.getItem("access-token");

    return (
        <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID}>
            <RecurlyProvider publicKey={process.env.REACT_APP_ENVIRONMENT === "PROD" ? process.env.REACT_APP_RECURLY_PUBLIC_KEY : process.env.REACT_APP_RECURLY_PUBLIC_KEY_DEV} />
            <BrowserRouter>
                {(!isMobile && tkn) && (
                    <Helmet>
                        <meta name="apple-itunes-app" content="app-id=6444163742" />
                        <meta name="google-play-app" content="app-id=com.indimoves.indi" />
                        <meta name="smartbanner:title" content="Indi - Workouts At Home" />
                        <meta name="smartbanner:author" content="Indi-Digital Wellness, LLC" />
                        <meta name="smartbanner:icon-apple" content="https://is1-ssl.mzstatic.com/image/thumb/Purple221/v4/ab/27/17/ab271745-456a-5aae-5290-886833c51d66/AppIcon-1x_U007emarketing-0-7-0-85-220.png/1024x1024bb.png" />
                        <meta name="smartbanner:icon-google" content="https://lh3.googleusercontent.com/M0ZlsIIwNPuLQlnPL6JGfQ3NupBnUa7L-XWZ2Lkcib4nIxQ54PN-IqRz8GAo9St10LI" />
                        <meta name="smartbanner:button" content="VIEW" />
                        <meta name="smartbanner:button-url-apple" content="https://apps.apple.com/us/app/indi-get-moving/id6444163742" />
                        <meta name="smartbanner:button-url-google" content="https://play.google.com/store/apps/details?id=com.indimoves.indi" />
                        <meta name="smartbanner:enabled-platforms" content="android,ios" />
                        <meta name="smartbanner:close-label" content="Close" />
                        <meta name="smartbanner:exclude-user-agent-regex" content="^.*(Version).*Safari"></meta>
                    </Helmet>
                )}

                <RecurlyProvider publicKey={process.env.REACT_APP_ENVIRONMENT === "PROD" ? process.env.REACT_APP_RECURLY_PUBLIC_KEY : process.env.REACT_APP_RECURLY_PUBLIC_KEY_DEV}>
                    <Elements>
                        <Routes />
                    </Elements>
                </RecurlyProvider>
                <Toaster {...TOAST_CONFIGS} children={CustomToaster} />

                {(!isAndroid && account?.billingInfo?.hasLiveSubscription) && (
                    <CustomSlider show={showSlider} setShowSlider={false}>
                        {showSlider && (
                            <AppNotifPermPrompt
                                type="existing"
                                closeSlider={() => {
                                    setShowSlider(false);
                                }}
                            />
                        )}
                    </CustomSlider>
                )}

                <CustomSlider
                    showCloseButton={showShareButton}
                    show={showInAppNotificationOverlay}
                    setShowSlider={() => {
                        props.SetShowInAppNotificationOverlay(false);
                        props.InAppNotificationClicked(inAppNotificationOverlayData?.ianID);
                    }}
                >
                    <InAppNotification showShareButton={showShareButton} setShowShareButton={setShowShareButton} {...inAppNotificationOverlayData?.metadata?.appearance} ianID={inAppNotificationOverlayData?.ianID} />
                </CustomSlider>
            </BrowserRouter>
        </GoogleOAuthProvider>
    );
};

const mapStateToProps = (state) => ({
    User: state.User,
    Cast: state.Cast,
    Utils: state.Utils,
});

export default connect(mapStateToProps, {
    LoadUserData,
    GetWorkOutHistory,
    LoadUserBillingData,
    GetPrograms,
    CastSocketTakeAction,
    CastDeviceCheck,
    UpdateAirplayAvailability,
    UpdateAirplayConnectivity,
    UpdateCastAvailability,
    SetDeviceType,
    GetInAppNotifications,
    InAppNotificationClicked,
    GetWorkoutDetails,
    SetShowLocationPromptOverlay,
    SetShowInAppNotificationOverlay,
    SetInAppNotificationOverlayData,
    GetEligibleLocations,
    SetVPTStatus,
    CheckLocationEligibility,
    AddUserLocation,
    SetLocation,
    LoadAccountDetails,
    SetInvitationDetails,
    GetPatientEmailDetails,
    NewError,
})(AppWrapper);
